/**
 * QuoteBuilder.jsx - "Build quote" from an opportunity: Quote Master Rev 3's
 * Service Quote (with its calendar) and Parts Quote, in GlobalOps.
 *
 * It opens over the opportunity editor, prefilled from the opportunity, its
 * account / site and contacts and the signed-in person (quoteDraft.js).
 * Everything typed is kept as a draft per opportunity (PUT /api/quotes/drafts,
 * a second after the last change and on close), so a long calendar is never
 * lost; reopening resumes it. "New revision" starts R1, R2... from the
 * current one (each revision's draft is kept).
 *
 * Every hour and dollar on screen comes from POST /api/quotes/calc - the same
 * engine that writes the document. Preview renders the generated .docx in the
 * browser (docx-preview, loaded on first use); Download gives the Word file,
 * or a PDF when the server has LibreOffice (501 and a plain message when not).
 *
 * Deliberately: nothing here saves the document to the opportunity or its Doc
 * Hub, and nothing changes the opportunity's quoted price (owner, 10/6/26).
 *
 * On the GLOBAL KSA tab (Shawn, 10/7/26) the same builder runs on KSA's own
 * rate card: every call goes to the tab's API base (useApiBase), so the KSA
 * card prices it - in Saudi Riyals, on a Saturday-to-Friday week - and the
 * draft is kept on the KSA record. The header says which card it is in.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDraggableDialog } from '../../hooks/useDraggableDialog';
import { useApp } from '../../context/AppContext';
import { useAuth } from '../../context/AuthContext';
import { useApiBase, useDataScope } from '../../context/DataScope';
import { canEdit } from '../../data/roles';
import { useMyAccess } from '../../hooks/useMyAccess';
import { cleanServiceDraft, newPartsDraft, newServiceDraft } from './quoteDraft';
import { newWorksheetDraft } from './worksheetDraft';
import { calcQuote, fetchDocument, getCapabilities, getDrafts, getQuoteSettings, getQuoteStaff, saveDraft } from './quoteApi';
import { downloadBlob } from '../../utils/exportCsv';
import ServiceQuoteForm from './ServiceQuoteForm';
import PartsQuoteForm from './PartsQuoteForm';
import WorksheetQuoteForm from './WorksheetQuoteForm';
import TotalsPanel from './TotalsPanel';
import DocPreview from './DocPreview';
import { Btn, GREEN, LINE, MUTED, PANEL, currencyName, currencyOf } from './quoteUi';

const SAVE_MS = 1200;
const CALC_MS = 300;
// The Worksheet quote (10/12/26, the GAT-B workbook) is the main builder's
// first and default kind; GLOBAL KSA's card has no level rates or staff costs.
const MAIN_TYPES = [
  ['worksheet', 'Worksheet quote'],
  ['service', 'Service quote'],
  ['parts', 'Parts quote'],
];
const KSA_TYPES = [
  ['service', 'Service quote'],
  ['parts', 'Parts quote'],
];
const newDraftOf = (t, ctx) => (t === 'parts' ? newPartsDraft(ctx) : t === 'worksheet' ? newWorksheetDraft(ctx) : newServiceDraft(ctx));
const timeOf = (d) => (d ? new Date(d).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }) : '');

export default function QuoteBuilder({ opp, form, onClose }) {
  const drag = useDraggableDialog(); // moved by its green header (10/9/26)
  const { allAccounts } = useApp();
  const { user } = useAuth();
  const access = useMyAccess();
  const canOverride = canEdit(access, 'quotes');
  const projectId = opp && opp.id;
  // which builder this is: the main one, or GLOBAL KSA's on its own card
  const base = useApiBase();
  const scope = useDataScope();
  const TYPES = scope ? KSA_TYPES : MAIN_TYPES;

  const [phase, setPhase] = useState('loading'); // loading | ready | failed
  const [loadError, setLoadError] = useState('');
  const [rates, setRates] = useState(null); // { settings, catalog }
  const [pdfReady, setPdfReady] = useState(false);
  const [type, setType] = useState(scope ? 'service' : 'worksheet');
  // the Worksheet quote's people (names and levels, never pay) and whether this person sees costs
  const [staff, setStaff] = useState({ list: [], seesCosts: false });
  // per type: { revision, data, dirty, savedAt, saving, saveError, revisions }
  const [drafts, setDrafts] = useState({});
  const [calcs, setCalcs] = useState({});
  const [calcBusy, setCalcBusy] = useState(false);
  const [calcError, setCalcError] = useState('');
  const [docBusy, setDocBusy] = useState('');
  const [docError, setDocError] = useState('');
  const [preview, setPreview] = useState(null); // { blob, name }
  const previewDrag = useDraggableDialog(!!preview);

  /* ── load: rates, saved drafts, can the server make PDFs ── */
  useEffect(() => {
    let live = true;
    (async () => {
      const [r, d, c, st] = await Promise.all([
        getQuoteSettings(base),
        getDrafts(projectId, base),
        getCapabilities(base),
        scope ? Promise.resolve(null) : getQuoteStaff(base),
      ]);
      if (!live) return;
      if (!r.ok || !r.body || !r.body.settings) {
        setLoadError((r.body && r.body.error) || 'Could not load the quote rates.');
        setPhase('failed');
        return;
      }
      const settings = r.body.settings;
      const ctx = { opp, form, accounts: allAccounts, user, settings };
      const saved = (d.ok && d.body) || {};
      const next = {};
      for (const [t] of TYPES) {
        const s = saved[t];
        next[t] = s
          ? { revision: s.revision, data: s.data, dirty: false, savedAt: s.updatedAt, revisions: s.revisions || [s.revision] }
          : { revision: 0, data: newDraftOf(t, ctx), dirty: false, savedAt: null, revisions: [] };
      }
      setRates({ settings, catalog: r.body.catalog || [], seesRates: !!r.body.seesRates });
      setDrafts(next);
      setPdfReady(!!(c.ok && c.body && c.body.pdf));
      if (st && st.ok && st.body) setStaff({ list: st.body.staff || [], seesCosts: !!st.body.seesCosts });
      // open on the kind already worked on (an old Service draft stays where it was)
      const first = TYPES.map(([t]) => t).find((t) => saved[t]);
      if (first) setType(first);
      setPhase('ready');
    })();
    return () => {
      live = false;
    };
    // opened once per opportunity; later edits to the form do not re-prefill
  }, [projectId]); // eslint-disable-line react-hooks/exhaustive-deps

  const cur = drafts[type];
  const settings = rates && rates.settings;
  const setDraft = useCallback(
    (fn) =>
      setDrafts((all) => {
        const d = all[type];
        const data = typeof fn === 'function' ? fn(d.data) : fn;
        return { ...all, [type]: { ...d, data, dirty: true, saveError: '' } };
      }),
    [type]
  );

  /* ── save: a moment after the last change ── */
  const dataFor = useCallback((t, data) => (t === 'service' ? cleanServiceDraft(data, settings) : data), [settings]);
  const persist = useCallback(
    async (t, d) => {
      if (!d) return true;
      setDrafts((all) => ({ ...all, [t]: { ...all[t], saving: true } }));
      const res = await saveDraft(projectId, t, d.revision, dataFor(t, d.data), base);
      setDrafts((all) => {
        const now = all[t];
        const same = now.data === d.data;
        return {
          ...all,
          [t]: res.ok
            ? {
                ...now,
                saving: false,
                dirty: !same,
                savedAt: res.body && res.body.updatedAt,
                revisions: Array.from(new Set([...(now.revisions || []), d.revision])).sort((a, b) => a - b),
              }
            : { ...now, saving: false, saveError: (res.body && res.body.error) || 'Could not save the draft.' },
        };
      });
      return res.ok;
    },
    [projectId, dataFor, base]
  );
  useEffect(() => {
    if (!cur || !cur.dirty || cur.saving) return undefined;
    const t = setTimeout(() => persist(type, cur), SAVE_MS);
    return () => clearTimeout(t);
  }, [cur, type, persist]);

  /* ── live totals ── */
  const calcSeq = useRef(0);
  const curData = cur && cur.data;
  useEffect(() => {
    if (!curData || phase !== 'ready') return undefined;
    const seq = ++calcSeq.current;
    setCalcBusy(true);
    const t = setTimeout(async () => {
      const r = await calcQuote(type, curData, base);
      if (seq !== calcSeq.current) return;
      setCalcBusy(false);
      if (r.ok) {
        setCalcs((c) => ({ ...c, [type]: r.body }));
        setCalcError('');
      } else setCalcError((r.body && r.body.error) || 'Could not work out the totals.');
    }, CALC_MS);
    return () => clearTimeout(t);
  }, [curData, type, phase, base]);

  /* ── close: save what is unsaved first; Esc closes this, never the editor under it ── */
  const closing = useRef(false);
  const close = useCallback(async () => {
    if (closing.current) return;
    closing.current = true;
    for (const [t] of TYPES) if (drafts[t] && drafts[t].dirty) await persist(t, drafts[t]);
    onClose();
  }, [drafts, persist, onClose, TYPES]);
  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      e.stopPropagation();
      if (preview) setPreview(null);
      else close();
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [preview, close]);

  const newRevision = async () => {
    if (!cur) return;
    const next = Math.max(cur.revision, ...(cur.revisions || [0])) + 1;
    if (!window.confirm(`Start revision R${next}? R${cur.revision} stays saved as it is.`)) return;
    const data = { ...cur.data, header: { ...cur.data.header, rev: next } };
    const d = { ...cur, revision: next, data, dirty: true };
    setDrafts((all) => ({ ...all, [type]: d }));
    await persist(type, d);
  };

  const getDoc = async (kind) => {
    setDocError('');
    setDocBusy(kind);
    const r = await fetchDocument(kind, type, cur.data, base);
    setDocBusy('');
    if (!r.ok) {
      setDocError(
        r.status === 501
          ? 'PDF needs LibreOffice on the server, which is not installed yet. Download the Word file instead (Word can save it as PDF).'
          : r.error
      );
      return null;
    }
    return r;
  };
  const doPreview = async () => {
    const r = await getDoc('docx');
    if (r) setPreview(r);
  };
  const doDownload = async (kind) => {
    const r = await getDoc(kind);
    if (r) downloadBlob(r.blob, r.name);
  };

  const calc = calcs[type] || null;
  const status = useMemo(() => {
    if (!cur) return '';
    if (cur.saveError) return cur.saveError;
    if (cur.saving) return 'Saving draft...';
    if (cur.dirty) return 'Unsaved changes';
    return cur.savedAt ? `Draft saved ${timeOf(cur.savedAt)}` : 'New - not saved yet';
  }, [cur]);
  const cur$ = currencyOf(settings);
  // "KSA - Saudi Riyals (SAR)": the card this builder prices on, on the KSA tab
  // (and on main whenever its card is not in US dollars)
  const cardName = scope || cur$.code !== 'USD' ? `${scope ? 'KSA' : 'GLOBAL'} - ${currencyName(cur$)}` : '';
  // a KSA record kept in another currency is still quoted in the card's
  const recCurrency = String((form && form.currency) || (opp && opp.currency) || '')
    .trim()
    .toUpperCase();
  const currencyNote =
    scope && recCurrency && settings && recCurrency !== cur$.code
      ? `This record is in ${recCurrency}; the quote is priced in ${cur$.code} from the KSA rate card (nothing is converted).`
      : '';
  const title = [
    form && form.quoteLetter ? `Q${form.quoteLetter}` : '',
    (form && (form.name || form.topic)) || (opp && (opp.name || opp.customer)) || '',
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Build quote"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 1200,
        background: 'rgba(0,0,0,0.35)',
        display: 'flex',
        alignItems: 'stretch',
        justifyContent: 'center',
        padding: 16,
        fontFamily: 'Montserrat',
      }}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) close();
      }}
    >
      <div
        ref={drag.ref}
        onPointerDown={drag.onPointerDown}
        className="go-pop-in"
        style={{
          background: PANEL,
          borderRadius: 12,
          width: '100%',
          maxWidth: 1500,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
          ...drag.style,
        }}
      >
        {/* header */}
        <div
          data-dialog-handle=""
          style={{
            background: 'linear-gradient(135deg, #1B5E20, #2E7D32)',
            color: '#fff',
            padding: '12px 18px',
            display: 'flex',
            alignItems: 'center',
            gap: 14,
            flexWrap: 'wrap',
          }}
        >
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: 1.4, opacity: 0.8, textTransform: 'uppercase' }}>
              Build quote
              {cardName && (
                <span
                  data-testid="quote-card-name"
                  style={{ marginLeft: 8, padding: '1px 7px', borderRadius: 4, background: 'rgba(255,255,255,0.22)', letterSpacing: 0.6 }}
                >
                  {cardName}
                </span>
              )}
            </div>
            <div
              style={{ fontSize: 14, fontWeight: 800, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 520 }}
            >
              {title || 'Opportunity'}
            </div>
          </div>
          <div role="tablist" style={{ display: 'flex', background: 'rgba(255,255,255,0.15)', borderRadius: 8, padding: 3, gap: 2 }}>
            {TYPES.map(([t, label]) => (
              <button
                key={t}
                role="tab"
                aria-selected={type === t}
                type="button"
                onClick={() => {
                  setType(t);
                  setDocError('');
                }}
                style={{
                  padding: '6px 14px',
                  borderRadius: 6,
                  border: 'none',
                  cursor: 'pointer',
                  fontFamily: 'Montserrat',
                  fontSize: 11,
                  fontWeight: 700,
                  background: type === t ? '#fff' : 'transparent',
                  color: type === t ? GREEN : '#fff',
                }}
              >
                {label}
                {drafts[t] && drafts[t].revisions && drafts[t].revisions.length ? ` R${drafts[t].revision}` : ''}
              </button>
            ))}
          </div>
          <span style={{ fontSize: 10.5, opacity: 0.9, marginLeft: 'auto' }}>{status}</span>
          <Btn small onClick={newRevision} disabled={phase !== 'ready'} title="Keep this revision and start the next (R1, R2...)">
            New revision
          </Btn>
          <Btn small onClick={doPreview} disabled={phase !== 'ready' || !!docBusy}>
            {docBusy === 'docx' && !preview ? 'Building...' : 'Preview'}
          </Btn>
          <Btn small onClick={() => doDownload('docx')} disabled={phase !== 'ready' || !!docBusy}>
            Download Word
          </Btn>
          <Btn
            small
            onClick={() => doDownload('pdf')}
            disabled={phase !== 'ready' || !!docBusy}
            title={pdfReady ? 'Converted on the server with LibreOffice' : 'Needs LibreOffice on the server (not installed yet)'}
          >
            {docBusy === 'pdf' ? 'Converting...' : 'Download PDF'}
          </Btn>
          <Btn small onClick={close} aria-label="Close the quote builder">
            Close
          </Btn>
        </div>
        {currencyNote && phase === 'ready' && (
          <div
            role="status"
            style={{
              padding: '6px 18px',
              background: '#FFF8E1',
              borderBottom: '1px solid #FDE68A',
              color: '#92400E',
              fontSize: 11.5,
              fontWeight: 600,
            }}
          >
            {currencyNote}
          </div>
        )}
        {docError && (
          <div
            role="alert"
            style={{
              padding: '8px 18px',
              background: '#FFEBEE',
              borderBottom: '1px solid #FFCDD2',
              color: '#B71C1C',
              fontSize: 12,
              fontWeight: 600,
            }}
          >
            {docError}
          </div>
        )}

        {/* body */}
        {phase === 'loading' && (
          <div style={{ padding: 40, textAlign: 'center', color: MUTED, fontSize: 12 }}>Loading the rates and any saved draft...</div>
        )}
        {phase === 'failed' && (
          <div role="alert" style={{ padding: 40, textAlign: 'center', color: '#B71C1C', fontSize: 12, fontWeight: 600 }}>
            {loadError}
          </div>
        )}
        {phase === 'ready' && cur && (
          <div style={{ flex: 1, minHeight: 0, display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 300px', gap: 16, padding: 16 }}>
            <div style={{ overflowY: 'auto', minHeight: 0, paddingRight: 4 }}>
              {type === 'worksheet' ? (
                <WorksheetQuoteForm
                  draft={cur.data}
                  setDraft={setDraft}
                  settings={settings}
                  calc={calc}
                  canOverride={canOverride}
                  staff={staff.list}
                  seesCosts={calc ? !!calc.seesCosts : staff.seesCosts}
                />
              ) : type === 'service' ? (
                <ServiceQuoteForm draft={cur.data} setDraft={setDraft} settings={settings} calc={calc} canOverride={canOverride} />
              ) : (
                <PartsQuoteForm
                  draft={cur.data}
                  setDraft={setDraft}
                  settings={settings}
                  catalog={rates.catalog}
                  calc={calc}
                  canOverride={canOverride}
                />
              )}
            </div>
            <div style={{ overflowY: 'auto', minHeight: 0 }}>
              <TotalsPanel type={type} calc={calc} busy={calcBusy} error={calcError} showRate={!!rates.seesRates} currency={cur$} />
              <div
                style={{
                  marginTop: 12,
                  fontSize: 10.5,
                  color: MUTED,
                  lineHeight: 1.5,
                  border: `1px dashed ${LINE}`,
                  borderRadius: 8,
                  padding: 10,
                }}
              >
                Your inputs are saved as a draft on this {scope ? 'KSA record' : 'opportunity'}. The document itself is not saved anywhere
                and the
                {scope ? ' record' : ' opportunity'}'s quoted price is not changed: download it and add it to the Doc Hub yourself.
              </div>
            </div>
          </div>
        )}
      </div>

      {preview && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 1250,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            justifyContent: 'center',
            padding: 16,
          }}
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) setPreview(null);
          }}
        >
          <div
            ref={previewDrag.ref}
            onPointerDown={previewDrag.onPointerDown}
            className="go-pop-in"
            style={{
              background: '#E9ECE6',
              borderRadius: 12,
              width: '100%',
              maxWidth: 980,
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
              ...previewDrag.style,
            }}
          >
            <div
              data-dialog-handle=""
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                padding: '10px 14px',
                background: '#fff',
                borderBottom: `1px solid ${LINE}`,
              }}
            >
              <span style={{ fontSize: 12, fontWeight: 800, color: GREEN }}>Preview · {preview.name}</span>
              <span style={{ fontSize: 10.5, color: MUTED }}>Close to the Word file; the download is the real thing.</span>
              <span style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
                <Btn small primary onClick={() => downloadBlob(preview.blob, preview.name)}>
                  Download Word
                </Btn>
                <Btn small onClick={() => setPreview(null)}>
                  Close preview
                </Btn>
              </span>
            </div>
            <div style={{ flex: 1, overflow: 'auto' }}>
              <DocPreview blob={preview.blob} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
